import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { Crown, AlertTriangle, CheckCircle2, Receipt } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Link } from "react-router-dom";

interface SubscriptionManagementProps {
  userId: string;
  status: string;
}

export function SubscriptionManagement({ userId, status }: SubscriptionManagementProps) {
  const [isLoading, setIsLoading] = useState(false);
  const [subscriptionData, setSubscriptionData] = useState<any>(null);
  const { toast } = useToast();

  useEffect(() => {
    if (status === 'premium' || status === 'church') {
      fetchSubscriptionData();
    }
  }, [userId, status]);

  const fetchSubscriptionData = async () => {
    const { data } = await supabase
      .from('subscriptions')
      .select('*')
      .eq('user_id', userId)
      .single();
    
    if (data) {
      setSubscriptionData(data);
    }
  };

  const handleCancelSubscription = async () => {
    setIsLoading(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
      
      const response = await fetch(
        `${supabaseUrl}/functions/v1/cancel-subscription`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${session?.access_token}`,
            'Content-Type': 'application/json',
          },
        }
      );

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.error || 'Failed to cancel subscription');
      }

      toast({
        title: "Subscription Canceled",
        description: `Your subscription will remain active until ${new Date(result.canceledAt).toLocaleDateString()}. You won't be charged again.`,
      });

      fetchSubscriptionData();
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message || "Failed to cancel subscription. Please contact support.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  if (status === 'free') {
    return null;
  }

  const getPlanName = (status: string) => {
    return status === 'church' ? 'Church' : 'Premium';
  };

  const periodEnd = subscriptionData?.current_period_end 
    ? new Date(subscriptionData.current_period_end).toLocaleDateString() 
    : null;

  return (
    <div className="border-t border-app-border pt-6">
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Crown className="w-5 h-5 text-app-accent" />
            <h3 className="text-lg font-semibold text-app-text-primary">Manage Subscription</h3>
          </div>
          <Badge variant="outline" className="border-app-accent text-app-accent">
            {getPlanName(status)} Active
          </Badge>
        </div>
        
        {subscriptionData?.cancel_at_period_end ? (
          <div className="flex items-start gap-3 p-4 rounded-lg bg-amber-500/10 border border-amber-500/20">
            <AlertTriangle className="w-5 h-5 text-amber-500 mt-0.5" />
            <div className="flex-1">
              <p className="font-medium text-app-text-primary">Subscription Ending</p>
              <p className="text-sm text-app-text-secondary mt-1">
                Your subscription will end on {periodEnd}. You can continue using premium features until then.
              </p>
            </div>
          </div>
        ) : (
          <>
            {periodEnd && (
              <div className="flex items-start gap-3 p-4 rounded-lg bg-app-accent/10 border border-app-accent/20">
                <CheckCircle2 className="w-5 h-5 text-app-accent mt-0.5" />
                <div className="flex-1">
                  <p className="font-medium text-app-text-primary">Active Subscription</p>
                  <p className="text-sm text-app-text-secondary mt-1">
                    Your next billing date is {periodEnd}
                  </p>
                </div>
              </div>
            )}
            
            <Link to="/billing-history">
              <Button 
                variant="outline" 
                className="w-full border-app-border text-app-text-primary hover:bg-app-surface mb-4"
              >
                <Receipt className="w-4 h-4 mr-2" />
                View Billing History
              </Button>
            </Link>
            
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button 
                  variant="outline" 
                  className="w-full border-destructive text-destructive hover:bg-destructive hover:text-white"
                  disabled={isLoading}
                >
                  Cancel Subscription
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent className="bg-app-surface border-app-border">
                <AlertDialogHeader>
                  <AlertDialogTitle className="text-app-text-primary">Are you sure?</AlertDialogTitle>
                  <AlertDialogDescription className="text-app-text-secondary">
                    Your subscription will remain active until {periodEnd}, and you won't be charged again. 
                    You can resubscribe anytime.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel className="border-app-border text-app-text-primary">
                    Keep Subscription
                  </AlertDialogCancel>
                  <AlertDialogAction
                    onClick={handleCancelSubscription}
                    className="bg-destructive text-white hover:bg-destructive/90"
                    disabled={isLoading}
                  >
                    {isLoading ? 'Canceling...' : 'Cancel Subscription'}
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </>
        )}
      </div>
    </div>
  );
}
